import {Injectable} from '@angular/core';
import {AbstractControl, ValidatorFn, Validators} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';

/** Marque posée sur un contrôle code NUTS qui porte déjà le validateur de cohérence avec le pays. */
const NUTS_OF_COUNTRY_VALIDATOR_MARK = '__nutsOfCountryValidator';

/**
 * Filtrage des codes NUTS par pays.
 *
 * Les sous-listes NUTS par pays proviennent des libellés i18n, sous la forme
 * `eform-not-editable.code|name|nuts-<alpha3>-lvl3.<CODE>` (37 pays).
 *
 * L'appartenance d'un code NUTS à un pays se lit dans ces sous-listes et non par
 * troncature alpha-3 → alpha-2 : le préfixe NUTS diffère du code pays pour 14 des
 * 37 pays (GRC → EL, AUT → AT, SVN → SI, SWE → SE…).
 *
 * Le service est fourni à la racine et la map n'est construite qu'une fois, alors
 * que le composant form-input est instancié une fois par champ.
 */
@Injectable({
  providedIn: 'root'
})
export class NutsCountryFilterService {

  /** Couples champ « code NUTS » → champ « pays » qui le conditionne. */
  private static readonly NUTS_TO_COUNTRY_FIELD: { [nutsFieldId: string]: string } = {
    'BT-5071-Procedure': 'BT-5141-Procedure',
    'BT-5071-Lot': 'BT-5141-Lot',
    'BT-5071-Part': 'BT-5141-Part',
    'BT-507-Organization-Company': 'BT-514-Organization-Company',
    'BT-507-Organization-TouchPoint': 'BT-514-Organization-TouchPoint',
    'BT-507-UBO': 'BT-514-UBO',
    'BT-507-Business': 'BT-514-Business'
  };

  /**
   * Chemin du contrôle pays depuis le groupe d'adresse (…address ou …postaladdress).
   * Ce groupe contient aussi le groupe du code NUTS (countrysubentitycode) : vérifié sur
   * les 52 formulaires, pour les sept couples ci-dessus.
   */
  private static readonly COUNTRY_CONTROL_PATH = ['country', 'identificationcode', 'val'];

  /** Codes NUTS par pays (alpha-3), construits à la demande depuis les libellés i18n. */
  private nutsCodesByCountry: Map<string, Set<string>> | null = null;

  constructor(private translateService: TranslateService) {
  }

  /**
   * Retourne l'identifiant du champ pays associé à un champ code NUTS,
   * ou null si le champ n'est pas un champ code NUTS.
   */
  getCountryFieldIdFor(nutsFieldId: string): string | null {
    if (!nutsFieldId) {
      return null;
    }
    return NutsCountryFilterService.NUTS_TO_COUNTRY_FIELD[nutsFieldId] || null;
  }

  /**
   * Restreint une liste de codes NUTS à ceux du pays passé en paramètre.
   *
   * @param nutsOptions liste complète des codes NUTS ({code, libelle})
   * @param countryCode code pays alpha-3 (ex : 'FRA')
   * @returns la sous-liste du pays ; une liste vide si le pays est absent,
   *          inconnu, ou n'appartient pas à la zone NUTS (ex : 'GBR')
   */
  filterNutsByCountry(nutsOptions: any[], countryCode: string): any[] {
    if (!nutsOptions || nutsOptions.length === 0) {
      return [];
    }
    if (!countryCode) {
      return [];
    }

    const codesForCountry = this.getNutsCodesByCountry().get(countryCode.toUpperCase());
    if (!codesForCountry || codesForCountry.size === 0) {
      return [];
    }

    return nutsOptions.filter(option => option && codesForCountry.has(option.code));
  }

  /**
   * Indique si un code NUTS appartient au pays passé en paramètre.
   *
   * @param nutsCode code NUTS (ex : 'FR101')
   * @param countryCode code pays alpha-3 (ex : 'FRA')
   * @returns false pour un pays sans codes NUTS (ex : 'GBR') ; null si la réponse
   *          n'est pas connue : code ou pays non renseigné, ou sous-listes NUTS
   *          pas encore chargées
   */
  isNutsCodeOfCountry(nutsCode: string, countryCode: string): boolean | null {
    if (!nutsCode || !countryCode) {
      return null;
    }
    const nutsCodesByCountry = this.getNutsCodesByCountry();
    if (nutsCodesByCountry.size === 0) {
      return null;
    }
    const codesForCountry = nutsCodesByCountry.get(countryCode.toUpperCase());
    return !!codesForCountry && codesForCountry.has(nutsCode);
  }

  /**
   * Retourne le code pays associé à un code NUTS, lu dans le contrôle pays du même groupe
   * d'adresse : c'est le pays de l'instance affichée (organisation, UBO, lot, partie, lieu
   * d'exécution). La table des champs à plat (flatFields) ne distingue pas les organisations
   * entre elles.
   *
   * @param nutsGroup groupe du code NUTS (countrysubentitycode), qui contient le contrôle val
   * @returns le code alpha-3, ou null si le pays n'est pas renseigné ou que son contrôle
   *          n'est pas encore créé
   */
  getCountryCode(nutsGroup: AbstractControl): string | null {
    const value = nutsGroup?.parent?.get(NutsCountryFilterService.COUNTRY_CONTROL_PATH)?.value;
    return value ? ('' + value) : null;
  }

  /**
   * Pose sur le contrôle code NUTS un validateur qui signale (invalidReferenceValue) un code
   * n'appartenant pas au pays de la même adresse, pays sans codes NUTS compris.
   *
   * Le validateur lit le pays au moment de la validation et ne dépend d'aucun composant : il
   * reste juste quand la factory réutilise le contrôle pour une nouvelle instance de form-input.
   * Il n'est posé qu'une fois par contrôle. Il ne signale rien quand la réponse n'est pas
   * connue (pays non renseigné, sous-listes NUTS pas encore chargées).
   */
  addNutsOfCountryValidator(nutsControl: AbstractControl): void {
    if (!nutsControl || (nutsControl as any)[NUTS_OF_COUNTRY_VALIDATOR_MARK]) {
      return;
    }
    const nutsOfCountryValidator: ValidatorFn = (control: AbstractControl) => {
      const value = control.value;
      if (value == null || value === '') {
        return null;
      }
      const isOfCountry = this.isNutsCodeOfCountry('' + value, this.getCountryCode(control.parent));
      return isOfCountry === false ? {invalidReferenceValue: true} : null;
    };
    nutsControl.setValidators(nutsControl.validator
      ? Validators.compose([nutsControl.validator, nutsOfCountryValidator])
      : nutsOfCountryValidator);
    (nutsControl as any)[NUTS_OF_COUNTRY_VALIDATOR_MARK] = true;
  }

  /**
   * Construit (une fois) la map alpha-3 → codes NUTS à partir des clés de traduction.
   * Tant que les libellés ne sont pas chargés, rien n'est mis en cache.
   */
  private getNutsCodesByCountry(): Map<string, Set<string>> {
    if (this.nutsCodesByCountry != null && this.nutsCodesByCountry.size !== 0) {
      return this.nutsCodesByCountry;
    }

    const byCountry = new Map<string, Set<string>>();

    try {
      const currentLang = this.translateService.currentLang || 'fr';
      const translations = this.translateService.translations[currentLang]['eform-not-editable'] || {};
      const prefix = 'code|name|nuts-';
      const suffix = '-lvl3.';

      Object.keys(translations).forEach(key => {
        if (!key.startsWith(prefix)) {
          return;
        }
        const suffixIndex = key.indexOf(suffix, prefix.length);
        if (suffixIndex === -1) {
          return;
        }
        const countryCode = key.substring(prefix.length, suffixIndex).toUpperCase();
        // 'code|name|nuts-lvl3.XXX' (liste complète) ne porte pas de code pays
        if (!/^[A-Z]{3}$/.test(countryCode)) {
          return;
        }
        const nutsCode = key.substring(suffixIndex + suffix.length);
        if (!nutsCode) {
          return;
        }
        if (!byCountry.has(countryCode)) {
          byCountry.set(countryCode, new Set<string>());
        }
        byCountry.get(countryCode).add(nutsCode);
      });
    } catch (error) {
      return byCountry;
    }

    if (byCountry.size !== 0) {
      this.nutsCodesByCountry = byCountry;
    }
    return byCountry;
  }
}
